import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { CHUNK_LOAD_RELOAD_SESSION_KEY, isChunkLoadFailure } from '@/lib/chunk-load-errors';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isChunkLoadFailure(error) && typeof window !== 'undefined') {
      if (window.sessionStorage.getItem(CHUNK_LOAD_RELOAD_SESSION_KEY) !== '1') {
        window.sessionStorage.setItem(CHUNK_LOAD_RELOAD_SESSION_KEY, '1');
        window.location.reload();
        return;
      }
    }
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div
        role="alert"
        className="flex min-h-[300px] flex-col items-center justify-center gap-4 rounded-xl border border-red-500/30 bg-zinc-900/80 p-8 text-center"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10">
          <AlertTriangle className="h-7 w-7 text-red-400" />
        </div>

        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-white">
            Something went wrong
          </h2>
          <p className="max-w-md text-sm text-zinc-400">
            An unexpected error occurred while rendering this section.
            Please try again or contact support if the issue persists.
          </p>
        </div>

        {this.state.error && (
          <details className="w-full max-w-md text-start">
            <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-300">
              Error details
            </summary>
            <pre className="mt-2 overflow-auto rounded-md bg-zinc-950 p-3 text-xs text-red-300">
              {this.state.error.message}
            </pre>
          </details>
        )}

        <Button
          onClick={this.handleReset}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary-900 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </div>
    );
  }
}

export { ErrorBoundary };
export default ErrorBoundary;
